import React, { useState, useEffect, useRef } from 'react';
import '../ut_styles/timer.scss';
import SvgIcn from "../data/IconCompo";
import timeOverSound from '../assets/sounds/timeover.mp3'; // Import the sound file
import BackButton from '../components/BackButton';

const Timer = () => {
  const colors = 
  [
    "#FF4757", // Darker Soft Red
    "#FF6B6B", // Coral Red
    "#FFA07A", // Light Salmon
    "#FDE4CF", // Pale Peach
    "#FFD93D", // Bright Yellow
    "#CBCE91", // Sea Green
    "#4ECDC4", // Turquoise
    "#7BF1A8", // Mint Green
    "#A2D2FF", // Light Blue
    "#00BBF9", // Bright Sky Blue
    "#FAA0E6", // Light Pink
    "#AB83A1"  // Dusty Lavender
]

  ;
  const [selectedColor, setSelectedColor] = useState(() => {
    return localStorage.getItem("selectedColor") || colors[0];
  });
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem("isDarkMode") === "true";
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [countTitle, setCountTitle] = useState("Activity Name");
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [totalSeconds, setTotalSeconds] = useState(0);
  const [settingsHours, setSettingsHours] = useState(0);
  const [settingsMinutes, setSettingsMinutes] = useState(5); // Set default to 5 minutes
  const [settingsSeconds, setSettingsSeconds] = useState(0);
  const [selectedFont, setSelectedFont] = useState(() => {
    return localStorage.getItem('selectedFont') || "Roboto Flex";
  });

  const fonts = [
    "Roboto Flex",
    "Oswald",
    "Archivo Narrow",
    "Space Mono",
    "Special",
  ];

  const [audio] = useState(new Audio(timeOverSound)); // Create an Audio object
  const [showTimeOverPopup, setShowTimeOverPopup] = useState(false);
  const audioRef = useRef(new Audio(timeOverSound));

  const [playSoundOnTimeOver, setPlaySoundOnTimeOver] = useState(true);
  const [soundVolume, setSoundVolume] = useState(0.5);

  useEffect(() => {
    localStorage.setItem("selectedColor", selectedColor);
  }, [selectedColor]);

  useEffect(() => {
    localStorage.setItem("isDarkMode", isDarkMode);
  }, [isDarkMode]);

  useEffect(() => {
    let interval;
    if (isRunning && totalSeconds > 0) {
      interval = setInterval(() => {
        setTotalSeconds((prevSeconds) => {
          if (prevSeconds > 1) {
            return prevSeconds - 1;
          } else {
            clearInterval(interval);
            setIsRunning(false);
            setShowTimeOverPopup(true);
            playTimeOverSound();
            return 0;
          }
        });
      }, 1000);
    } else if (totalSeconds === 0) {
      setIsRunning(false);
    }
    return () => clearInterval(interval);
  }, [isRunning, totalSeconds]);

  useEffect(() => {
    audioRef.current.volume = soundVolume;
  }, [soundVolume]);

  const playTimeOverSound = () => {
    if (playSoundOnTimeOver) {
      audioRef.current.play();
      setTimeout(() => {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }, 20000);
    }
  };

  const handleStopTimeOver = () => {
    setShowTimeOverPopup(false);
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
  };

  useEffect(() => {
    setHours(Math.floor(totalSeconds / 3600));
    setMinutes(Math.floor((totalSeconds % 3600) / 60));
    setSeconds(totalSeconds % 60);
  }, [totalSeconds]);

  useEffect(() => {
    const initialColor = localStorage.getItem("selectedColor") || colors[0];
    const rgb = hexToRgb(initialColor);
    if (rgb) {
      document.documentElement.style.setProperty(
        "--selected-color-rgb",
        `${rgb.r}, ${rgb.g}, ${rgb.b}`
      );
    } else {
      console.warn(`Invalid color value: ${initialColor}`);
      // Set a default RGB value
      document.documentElement.style.setProperty(
        "--selected-color-rgb",
        "255, 107, 107"
      ); // #FF6B6B
    }
  }, []);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  const handleColorChange = (color) => {
    setSelectedColor(color);
    setShowColorPicker(false);

    // Convert hex to RGB
    const rgb = hexToRgb(color);
    document.documentElement.style.setProperty(
      "--selected-color-rgb",
      `${rgb.r}, ${rgb.g}, ${rgb.b}`
    );
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  const toggleColorPicker = () => {
    setShowColorPicker(!showColorPicker);
  };

  const toggleSettings = () => {
    setShowSettings(!showSettings);
  };

  const handleStartPause = () => {
    if (!isRunning && totalSeconds === 0) {
      const newTotalSeconds =
        settingsHours * 3600 + settingsMinutes * 60 + settingsSeconds;
      if (newTotalSeconds > 0) {
        setTotalSeconds(newTotalSeconds);
        setHours(settingsHours);
        setMinutes(settingsMinutes);
        setSeconds(settingsSeconds);
      } else {
        // Maybe show an alert or message that no time is set
        return;
      }
    }
    setIsRunning(!isRunning);
  };

  const handleReset = () => {
    setIsRunning(false);
    // Reset to the last set time instead of 0
    const lastSetSeconds = settingsHours * 3600 + settingsMinutes * 60 + settingsSeconds;
    setTotalSeconds(lastSetSeconds);
    setHours(settingsHours);
    setMinutes(settingsMinutes);
    setSeconds(settingsSeconds);
  };

  const handleSaveSettings = () => {
    setTotalSeconds(
      settingsHours * 3600 + settingsMinutes * 60 + settingsSeconds
    );
    setHours(settingsHours);
    setMinutes(settingsMinutes);
    setSeconds(settingsSeconds);
    setShowSettings(false);
  };

  const handleStartNow = () => {
    handleSaveSettings();
    setIsRunning(true);
  };

  const handleResetSettings = () => {
    // Clear inputs
    setCountTitle("");
    setSettingsHours(0);
    setSettingsMinutes(5); // Set default to 5 minutes
    setSettingsSeconds(0);

    // Stop the running timer
    setIsRunning(false);

    // Reset the timer on the top page to the default (5 minutes)
    setTotalSeconds(300); // 5 minutes = 300 seconds
    setHours(0);
    setMinutes(5);
    setSeconds(0);
  };

  // Modify the hexToRgb function to be more robust
  const hexToRgb = (hex) => {
    // Remove the hash if it's there
    hex = hex.replace(/^#/, "");

    // Parse the hex string
    const bigint = parseInt(hex, 16);

    // Check if the parsed value is a valid number
    if (isNaN(bigint)) {
      return null;
    }

    // Extract the RGB values
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    return { r, g, b };
  };

  const handleFontChange = (font) => {
    setSelectedFont(font);
    // Update the CSS variable for the selected font
    document.documentElement.style.setProperty("--selected-font", font);
    // Save the selected font to localStorage
    localStorage.setItem('selectedFont', font);
  };

  const handleSoundToggle = () => {
    setPlaySoundOnTimeOver(!playSoundOnTimeOver);
  };

  const handleVolumeChange = (e) => {
    setSoundVolume(parseFloat(e.target.value));
  };

  useEffect(() => {
    // Set initial total seconds to 5 minutes (300 seconds)
    setTotalSeconds(300);
    setHours(0);
    setMinutes(5);
    setSeconds(0);
    // Set default font
    const savedFont = localStorage.getItem('selectedFont') || "Roboto Flex";
    setSelectedFont(savedFont);
    document.documentElement.style.setProperty("--selected-font", savedFont);
  }, []); // This effect runs only once when the component mounts

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleStartNow();
    }
  };

  return (
    <div
      className={`timer-container ${isDarkMode ? "dark-mode" : ""}`}
      style={{ "--selected-color": selectedColor }}
    >
      <BackButton />
      <button
        className="mode-toggle-button"
        onClick={toggleDarkMode}
        title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
      >
        <SvgIcn
          name={isDarkMode ? "light" : "dark"}
          // hover={isDarkMode ? "icon06" : "icon07"}
        />
      </button>

      <button
        className="fullscreen-toggle-button"
        onClick={toggleFullscreen}
        title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
      >
        <SvgIcn
          name={isFullscreen ? "small" : "full"}
          hover={isFullscreen ? "icon02" : "icon03"}
        />
      </button>

      <div className="color-picker-container">
        <button
          className="color-picker-toggle-button"
          onClick={toggleColorPicker}
          title={showColorPicker ? "Hide Color Picker" : "Show Color Picker"}
        >
          <SvgIcn name="color" hover="icon06" />
        </button>

        {showColorPicker && (
          <div className="color-picker">
            {colors.map((color) => (
              <button
                key={color}
                className={`color-option ${
                  color === selectedColor ? "selected" : ""
                }`}
                style={{ backgroundColor: color }}
                onClick={() => handleColorChange(color)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="timer-content">
        <h1 className="count-title" style={{ fontFamily: selectedFont }}>
          {countTitle}
        </h1>

        <div className={`timer-display ${hours > 0 ? 'has-hours' : ''}`} style={{ fontFamily: selectedFont }}>
          {hours > 0 ? (
            <>
              <div className="time-unit">
                <div className="time-value">{hours.toString().padStart(2, "0")}</div>
                {/* <div className="time-label">Hours</div> */}
              </div>
              <SvgIcn name="separator" hover="icon06" className="time-separator_icon"/>
              <div className="time-unit">
                <div className="time-value">{minutes.toString().padStart(2, "0")}</div>
                {/* <div className="time-label">Minutes</div> */}
              </div>
              <SvgIcn name="separator" hover="icon06" className="time-separator_icon"/>
              <div className="time-unit">
                <div className="time-value">{seconds.toString().padStart(2, "0")}</div>
                {/* <div className="time-label">Seconds</div> */}
              </div>
            </>
          ) : (
            <>
              <div className="time-unit large">
                <div className="time-value">
                  {minutes.toString().padStart(2, "0")}
                </div>
                {/* <div className="time-label">Minutes</div> */}
              </div>
              {/* <div className="time-separator large">:</div> */}
              <SvgIcn name="separator" hover="icon06" className="time-separator_icon"/>
              <div className="time-unit large">
                <div className="time-value">
                  {seconds.toString().padStart(2, "0")}
                </div>
                {/* <div className="time-label">Seconds</div> */}
              </div>
            </>
          )}
        </div>

        <div className="timer-controls">
          <button className="control-button" onClick={handleStartPause}>
            {isRunning ? "Pause" : "Start"}
            <SvgIcn
              name={isRunning ? "pause2" : "start"}
              hover={isRunning ? "pause" : "close"}
            />
          </button>
          <button className="control-button" onClick={handleReset}>
            Reset
            <SvgIcn name="reset2" hover="icon05" />
          </button>
          <button className="control-button" onClick={toggleSettings}>
            Set
            <SvgIcn name="timer" hover="icon07" />
          </button>
        </div>
      </div>

      {showSettings && (
        <div className="lightbox">
          <div className="settings-popup">
            <button className="close-button" onClick={toggleSettings}>
              <SvgIcn name="close" hover="icon01" />
            </button>
            <div className="settings-content">
              <div className="input-group" style={{ fontFamily: selectedFont }}>
                <input
                  type="text"
                  value={countTitle}
                  onChange={(e) => setCountTitle(e.target.value.slice(0, 32))}
                  maxLength={32}
                  placeholder="Count Title"
                  style={{ fontFamily: selectedFont }}
                />
                <span className="char-count">{32 - countTitle.length}</span>
                <button
                  className="clear-button"
                  onClick={() => setCountTitle("")}
                >
                  <SvgIcn name="clear" hover="icon03" />
                </button>
              </div>
              <div className="time-inputs">
                <input
                  type="number"
                  value={settingsHours ? settingsHours.toString().padStart(2, "0") : ""}
                  onChange={(e) => {
                    const value = Math.min(24, Math.max(0, parseInt(e.target.value) || 0));
                    setSettingsHours(value);
                  }}
                  onKeyPress={handleKeyPress}
                  placeholder="00"
                />
                <input
                  type="number"
                  value={settingsMinutes ? settingsMinutes.toString().padStart(2, "0") : ""}
                  onChange={(e) => {
                    const value = Math.min(59, Math.max(0, parseInt(e.target.value) || 0));
                    setSettingsMinutes(value);
                  }}
                  onKeyPress={handleKeyPress}
                  placeholder="00"
                />
                <input
                  type="number"
                  value={settingsSeconds ? settingsSeconds.toString().padStart(2, "0") : ""}
                  onChange={(e) => {
                    const value = Math.min(59, Math.max(0, parseInt(e.target.value) || 0));
                    setSettingsSeconds(value);
                  }}
                  onKeyPress={handleKeyPress}
                  placeholder="00"
                />
              </div>
              <div className="font-selection">
                {/* <h3>Select Timer Font</h3> */}
                <div className="font-options">
                  {fonts.map((font) => (
                    <button
                      key={font}
                      className={`font-option ${
                        selectedFont === font ? "selected" : ""
                      }`}
                      style={{ fontFamily: font }}
                      onClick={() => handleFontChange(font)}
                    >
                      123
                    </button>
                  ))}
                </div>
              </div>
              <div className="sound-settings">
                <div className="sound-toggle">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={playSoundOnTimeOver}
                      onChange={handleSoundToggle}
                    />
                    <span className="slider round"></span>
                  </label>
                  <span>Play Sound</span>
                  <div className="volume-slider">
                    <SvgIcn name="volume-low" />
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={soundVolume}
                      onChange={handleVolumeChange}
                    />
                    <SvgIcn name="volume-high" />
                    <span className="volume-percentage">
                      {Math.round(soundVolume * 100)}%
                    </span>
                  </div>
                </div>
              </div>
              <div className="settings-buttons">
              <button onClick={handleResetSettings}>
                  Reset
                  <SvgIcn name="reset2" hover="icon01" />
                </button>
                <button onClick={handleSaveSettings}>
                  Save & Close
                  <SvgIcn name="ok" hover="icon05" />
                </button>
                <button onClick={handleStartNow}>
                  Start Now
                  <SvgIcn name="start" hover="icon07" />
                </button>

              </div>
            </div>
          </div>
        </div>
      )}

      {showTimeOverPopup && (
        <div className="lightbox">
          <div className="settings-popup time-over-popup">
            <h2 className="time-over-title">Time Over!</h2>
            <button className="control-button" onClick={handleStopTimeOver}>
              Stop
              <SvgIcn name="close" hover="icon01" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Timer;
